import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'

export type ApiClientName = 'api' | 'owner' | 'console'

export interface ApiMetric {
	id: string
	url: string
	method: string
	clientName: ApiClientName
	status?: number
	duration: number
	ttfb?: number
	download?: number
	// Pure network phases; absent whenever the connection or DNS result was reused.
	dns?: number
	connect?: number
	transferBytes?: number
	timestamp: number
	success: boolean
	errorMessage?: string
}

// Ring buffer cap: ~5000 entries × ~300 bytes ≈ 1.5 MB memory ceiling on long sessions.
const MAX_METRICS = 5000

interface DiagnosticsState {
	metrics: ApiMetric[]
	totalRequestsSeen: number
	enabled: boolean
}

interface DiagnosticsActions {
	addMetric: (metric: ApiMetric) => void
	clearMetrics: () => void
	setEnabled: (enabled: boolean) => void
}

type DiagnosticsStore = DiagnosticsState & DiagnosticsActions

export const useDiagnosticsStore = create<DiagnosticsStore>()(
	devtools(
		subscribeWithSelector(set => ({
			metrics: [],
			totalRequestsSeen: 0,
			enabled: true,
			addMetric: metric =>
				set(state => {
					if (!state.enabled) {
						return state
					}
					const next = state.metrics.length >= MAX_METRICS ? state.metrics.slice(-(MAX_METRICS - 1)) : state.metrics
					return {
						metrics: [...next, metric],
						totalRequestsSeen: state.totalRequestsSeen + 1,
					}
				}),
			clearMetrics: () => set({ metrics: [], totalRequestsSeen: 0 }),
			setEnabled: enabled => set({ enabled }),
		})),
		{ name: 'diagnostics-store' },
	),
)
